/**
 * ScreenerQuestion Component
 *
 * A standalone React component for rendering individual screener questions
 * dynamically based on question type and structure from findApplicationDrafts.
 *
 * Supports:
 * - SELECT questions (dropdown or radio buttons based on options)
 * - TEXT_BOX and TEXT_AREA questions (placeholder implementation)
 * - Dynamic validation and error handling
 * - Proper form field labeling and accessibility
 */

import React from 'react';
import { Box, Text, FormField, SelectList, RadioFormField, Option } from '@indeed/ifl-components';
import { getQuestionStructure } from './screenerQuestionsDataUtilities';

interface ScreenerQuestionProps {
    /** The question structure from findApplicationDrafts */
    question: ReturnType<typeof getQuestionStructure>[number];
    /** Current answer value for this question */
    currentAnswerValue: string;
    /** Whether this question has a validation error */
    hasError: boolean;
    /** Callback when the user changes their answer */
    onQuestionChange: (questionId: string, value: string) => void;
}

const ScreenerQuestion: React.FC<ScreenerQuestionProps> = ({
    question,
    currentAnswerValue,
    hasError,
    onQuestionChange,
}) => {
    const isRequired = question.requirement === 'REQUIRED' || question.requirement === 'required';

    const handleQuestionChange = (value: string) => {
        onQuestionChange(question.questionId, value);
    };

    // Determine if SELECT question should be rendered as radio buttons (for Yes/No type questions)
    const shouldRenderAsRadio =
        question.type === 'SELECT' &&
        question.options.length <= 3 &&
        question.options.some(
            (opt) =>
                opt.label?.includes('はい') ||
                opt.label?.includes('いいえ') ||
                opt.label?.includes('Yes') ||
                opt.label?.includes('No'),
        );

    switch (question.type) {
        case 'SELECT': {
            if (shouldRenderAsRadio) {
                // Render as radio buttons for Yes/No type questions
                const radioItems = question.options.map((option) => ({
                    value: option.value || '',
                    label: option.label || '',
                    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                        handleQuestionChange(event.target.value);
                    },
                    checked: currentAnswerValue === option.value,
                }));

                return (
                    <RadioFormField
                        key={question.questionId}
                        name={question.questionId}
                        items={radioItems}
                        label={question.label}
                        showRequiredIndicator={isRequired}
                        errorText={hasError ? `${question.label}の回答は必須です` : ''}
                        invalid={hasError}
                    />
                );
            } else {
                // Render as dropdown for multi-option questions
                return (
                    <FormField
                        key={question.questionId}
                        labelProps={{ id: `question-${question.questionId}` }}
                        label={question.label}
                        showRequiredIndicator={isRequired}
                        errorText={hasError ? `${question.label}の選択は必須です` : ''}
                        invalid={hasError}
                    >
                        <SelectList
                            placeholder="選択してください"
                            selectedValues={currentAnswerValue ? [currentAnswerValue] : []}
                            onSelectionChange={(selectedValues) => {
                                const value = selectedValues[0] ?? '';
                                handleQuestionChange(value);
                            }}
                            invalid={hasError}
                        >
                            {question.options.map((option) => (
                                <Option key={option.value} value={option.value || ''}>
                                    {option.label}
                                </Option>
                            ))}
                        </SelectList>
                    </FormField>
                );
            }
        }

        case 'TEXT_BOX':
        case 'TEXT_AREA': {
            return (
                <FormField
                    key={question.questionId}
                    labelProps={{ id: `question-${question.questionId}` }}
                    label={question.label}
                    showRequiredIndicator={isRequired}
                    errorText={hasError ? `${question.label}の入力は必須です` : ''}
                    invalid={hasError}
                >
                    {/* Text input would go here - for now showing placeholder */}
                    <Text sx={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}>
                        テキスト入力フィールド（未実装 - {question.type}）:{' '}
                        {currentAnswerValue || 'empty'}
                    </Text>
                </FormField>
            );
        }

        default: {
            return (
                <Box
                    key={question.questionId}
                    sx={{ padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}
                >
                    <Text>未対応の質問タイプ: {question.type}</Text>
                    <Text>{question.label}</Text>
                    {question.options.length > 0 && (
                        <Text sx={{ fontSize: 'small', color: 'secondary' }}>
                            オプション: {question.options.map((opt) => opt.label).join(', ')}
                        </Text>
                    )}
                </Box>
            );
        }
    }
};

export default ScreenerQuestion;
